/**
 * CSV import for measured sample data.
 *
 * The import is a two-step: parse and preview, then commit. Showing the
 * recovered statistics before anything is applied is the point — a delimiter
 * misread or a decimal-comma misread produces numbers that are obviously wrong
 * at a glance (a mean of 101 instead of 10.1) and invisible once they are
 * buried in a distribution.
 */

import { useRef, useState } from 'react';
import { parseCsv, describeSamples, type CsvParseResult } from '../../storage/csv.ts';
import { empiricalFromSamples } from '../../engine/distributions.ts';
import type { Distribution } from '../../engine/distributions.ts';
import { eng, count } from '../format.ts';
import { Info } from '../Info.tsx';

interface Props {
  unit?: string;
  onImport: (d: Distribution) => void;
}

export function MeasuredDataImport({ unit, onImport }: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [parsed, setParsed] = useState<CsvParseResult | null>(null);
  const [selected, setSelected] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    try {
      const result = parseCsv(await file.text());
      setParsed(result);
      setSelected(0);
    } catch (e) {
      setParsed(null);
      setError((e as Error).message);
    }
  };

  const column = parsed?.columns[selected];
  const stats = column ? describeSamples(column.values) : null;

  return (
    <div className="measured-import">
      <button type="button" className="add-btn" onClick={() => fileRef.current?.click()}>
        Import measured data (CSV)
      </button>
      <Info topic="measured-data" />
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.txt,.tsv,text/csv,text/plain"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = '';
        }}
      />

      {error && <p className="expr-error">{error}</p>}

      {parsed && column && stats && (
        <div className="import-preview">
          <div className="field-row">
            <label className="field">
              <span>Column</span>
              <select value={selected} onChange={(e) => setSelected(Number(e.target.value))}>
                {parsed.columns.map((c, i) => (
                  <option key={c.index} value={i}>
                    {c.name} ({count(c.values.length)} values)
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* The sanity check: if these numbers look wrong, the parse was wrong. */}
          <table className="data-table compact">
            <tbody>
              <tr>
                <th scope="row">Samples</th>
                <td>{count(stats.n)}</td>
                <th scope="row">Mean</th>
                <td>{eng(stats.mean, 5, unit)}</td>
              </tr>
              <tr>
                <th scope="row">Std dev</th>
                <td>{eng(stats.stdDev, 4, unit)}</td>
                <th scope="row">Median</th>
                <td>{eng(stats.median, 5, unit)}</td>
              </tr>
              <tr>
                <th scope="row">Min</th>
                <td>{eng(stats.min, 5, unit)}</td>
                <th scope="row">Max</th>
                <td>{eng(stats.max, 5, unit)}</td>
              </tr>
            </tbody>
          </table>

          {parsed.notes.map((n, i) => (
            <p key={i} className="callout warn small">
              {n}
            </p>
          ))}

          <div className="field-row">
            <button
              type="button"
              className="primary"
              onClick={() => {
                onImport(empiricalFromSamples(column.values));
                setParsed(null);
              }}
            >
              Use these {count(stats.n)} measurements
            </button>
            <button type="button" onClick={() => setParsed(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
